import React, { useEffect, useMemo, useState } from 'react';
import { AbaAtividadeDrawer, useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { ChecklistItem, Comentario, Evidencia, Excecao, LogAtividade, TipoExcecao } from '../models/atividade';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { formatarData } from '../utils/formatters';
import { LABEL_AREA, LABEL_PRIORIDADE, LABEL_PRODUTO, LABEL_TIPO_EXCECAO } from '../utils/labels';
import PainelInteligencia from './PainelInteligencia';
import ProdutoCard from './ProdutoCard';
import RiscoBadge from './RiscoBadge';
import StatusBadge from './StatusBadge';

const ABAS: Array<{ id: AbaAtividadeDrawer; titulo: string; icone: string }> = [
  { id: 'resumo', titulo: 'Resumo', icone: 'bi bi-info-circle' },
  { id: 'dependencias', titulo: 'Dependências', icone: 'bi bi-hourglass-split' },
  { id: 'checklist', titulo: 'Checklist', icone: 'bi bi-list-check' },
  { id: 'evidencias', titulo: 'Evidências', icone: 'bi bi-paperclip' },
  { id: 'excecoes', titulo: 'Exceções', icone: 'bi bi-shield-exclamation' },
  { id: 'comunicacao', titulo: 'Comunicação', icone: 'bi bi-chat-dots' },
  { id: 'historico', titulo: 'Histórico', icone: 'bi bi-clock-history' },
  { id: 'inteligencia', titulo: 'Inteligência', icone: 'bi bi-stars' },
  { id: 'produto', titulo: 'Produto', icone: 'bi bi-box' },
];

const AtividadeDrawer: React.FC = () => {
  const { atividadeSelecionadaId, abaDrawerInicial, fecharAtividade, atividades, dependencias, recomendacoes, produtosIntegrados, usuarios, abrirAtividade, getArea, getEmpresa, getUsuario } = useMaestroContext();
  const [aba, setAba] = useState<AbaAtividadeDrawer>(abaDrawerInicial);
  const [novoComentario, setNovoComentario] = useState('');
  const [novoItemDescricao, setNovoItemDescricao] = useState('');

  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [evidencias, setEvidencias] = useState<Evidencia[]>([]);
  const [historico, setHistorico] = useState<LogAtividade[]>([]);
  const [excecoes, setExcecoes] = useState<Excecao[]>([]);
  const [carregandoAbaDados, setCarregandoAbaDados] = useState(false);

  const [mostrarNovaExcecao, setMostrarNovaExcecao] = useState(false);
  const [novaExcecao, setNovaExcecao] = useState<{ tipo: TipoExcecao; dependenciaId: string; justificativa: string; autorizadorId: string; vigenciaAte: string }>({
    tipo: 'DEPENDENCIA_BLOQUEANTE', dependenciaId: '', justificativa: '', autorizadorId: '', vigenciaAte: '',
  });
  const [motivoRevogacao, setMotivoRevogacao] = useState<Record<number, string>>({});

  // Sempre que uma atividade é aberta (inclusive drill-downs que pedem uma aba específica,
  // como "ver contas com divergência" a partir do Gantt), a aba do drawer acompanha o pedido.
  useEffect(() => setAba(abaDrawerInicial), [atividadeSelecionadaId, abaDrawerInicial]);

  // Checklist/evidências/comentários/histórico são carregados sob demanda (não fazem parte da
  // listagem principal de atividades — carregar isso para todas as atividades do ciclo seria caro
  // e desnecessário fora do drawer).
  useEffect(() => {
    if (!atividadeSelecionadaId) return;
    let cancelado = false;

    async function carregar() {
      setCarregandoAbaDados(true);
      try {
        const [cl, co, ev, hi, ex] = await Promise.all([
          maestroService.listarChecklistPorAtividade(atividadeSelecionadaId as number),
          maestroService.listarComentariosPorAtividade(atividadeSelecionadaId as number),
          maestroService.listarEvidenciasPorAtividade(atividadeSelecionadaId as number),
          maestroService.listarHistoricoPorAtividade(atividadeSelecionadaId as number),
          maestroService.listarExcecoesPorAtividade(atividadeSelecionadaId as number),
        ]);
        if (cancelado) return;
        setChecklist(cl);
        setComentarios(co);
        setEvidencias(ev);
        setHistorico(hi);
        setExcecoes(ex);
      } catch {
        if (!cancelado) { setChecklist([]); setComentarios([]); setEvidencias([]); setHistorico([]); setExcecoes([]); }
      } finally {
        if (!cancelado) setCarregandoAbaDados(false);
      }
    }

    carregar();
    return () => { cancelado = true; };
  }, [atividadeSelecionadaId]);

  const atividade = useMemo(() => atividades.find(a => a.id === atividadeSelecionadaId), [atividades, atividadeSelecionadaId]);

  if (!atividade) return null;

  const area = getArea(atividade.areaId);
  const empresa = getEmpresa(atividade.empresaId);
  const responsavel = getUsuario(atividade.responsavelId);
  const revisor = atividade.revisorId ? getUsuario(atividade.revisorId) : undefined;
  const aprovador = atividade.aprovadorId ? getUsuario(atividade.aprovadorId) : undefined;

  const dependenciasAnteriores = dependencias.filter(d => atividade.dependenciaIds.includes(d.id));
  const dependenciasPosteriores = dependencias.filter(d => d.atividadeOrigemId === atividade.id);
  const recomendacoesDaAtividade = recomendacoes.filter(r => r.atividadeIds.includes(atividade.id));
  const produtoRelacionado = atividade.produtoIntegradoTipo
    ? produtosIntegrados.find(p => p.tipo === atividade.produtoIntegradoTipo && p.empresaId === atividade.empresaId)
    : undefined;

  const enviarComentario = () => {
    if (!novoComentario.trim()) return;
    maestroService.criarComentario(atividade.id, novoComentario.trim())
      .then(c => { setComentarios(prev => [...prev, c]); setNovoComentario(''); })
      .catch(() => dispararAcaoSimulada('Não foi possível registrar o comentário.'));
  };

  const criarItemChecklist = () => {
    if (!novoItemDescricao.trim()) return;
    maestroService.criarItemChecklist(atividade.id, novoItemDescricao.trim())
      .then(item => { setChecklist(prev => [...prev, item]); setNovoItemDescricao(''); })
      .catch(() => dispararAcaoSimulada('Não foi possível criar o item de checklist.'));
  };

  const concluirItemChecklist = (itemId: number) => {
    maestroService.atualizarStatusChecklistItem(itemId, 'CONCLUIDA')
      .then(item => setChecklist(prev => prev.map(i => (i.id === itemId ? item : i))))
      .catch(() => dispararAcaoSimulada('Não foi possível concluir o item.'));
  };

  const anexarArquivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    maestroService.anexarEvidencia(atividade.id, arquivo)
      .then(ev => setEvidencias(prev => [ev, ...prev]))
      .catch(() => dispararAcaoSimulada('Não foi possível anexar a evidência.'));
  };

  const concederExcecao = () => {
    if (!novaExcecao.justificativa.trim() || !novaExcecao.autorizadorId || !novaExcecao.vigenciaAte) return;
    maestroService.concederExcecao(atividade.id, {
      tipo: novaExcecao.tipo,
      dependenciaId: novaExcecao.tipo === 'DEPENDENCIA_BLOQUEANTE' && novaExcecao.dependenciaId ? Number(novaExcecao.dependenciaId) : undefined,
      justificativa: novaExcecao.justificativa.trim(),
      autorizadorId: Number(novaExcecao.autorizadorId),
      vigenciaAte: novaExcecao.vigenciaAte,
    })
      .then(exc => {
        setExcecoes(prev => [exc, ...prev]);
        setMostrarNovaExcecao(false);
        setNovaExcecao({ tipo: 'DEPENDENCIA_BLOQUEANTE', dependenciaId: '', justificativa: '', autorizadorId: '', vigenciaAte: '' });
      })
      .catch(() => dispararAcaoSimulada('Não foi possível conceder a exceção.'));
  };

  const revogarExcecao = (excecaoId: number) => {
    const motivo = (motivoRevogacao[excecaoId] ?? '').trim();
    if (!motivo) return;
    maestroService.revogarExcecao(excecaoId, motivo)
      .then(exc => setExcecoes(prev => prev.map(e => (e.id === excecaoId ? exc : e))))
      .catch(() => dispararAcaoSimulada('Não foi possível revogar a exceção.'));
  };

  return (
    <div className='mc-drawer-overlay' onClick={fecharAtividade}>
      <aside className='mc-drawer' onClick={e => e.stopPropagation()}>
        <div className='mc-drawer__topo'>
          <div>
            <span className='mc-drawer__codigo'>{atividade.codigo}</span>
            <h2>{atividade.caminhoCritico && <i className='bi bi-lightning-charge-fill mc-icone-critico' />} {atividade.nome}</h2>
          </div>
          <button type='button' className='mc-drawer__fechar' onClick={fecharAtividade}><i className='bi bi-x-lg' /></button>
        </div>

        <div className='mc-drawer__abas'>
          {ABAS.map(a => (
            <button key={a.id} type='button' className={aba === a.id ? 'is-active' : ''} onClick={() => setAba(a.id)}>
              <i className={a.icone} /> {a.titulo}
            </button>
          ))}
        </div>

        <div className='mc-drawer__conteudo'>
          {aba === 'resumo' && (
            <dl className='mc-drawer__resumo'>
              <div><dt>Empresa</dt><dd>{empresa?.nome}</dd></div>
              <div><dt>Área</dt><dd>{area ? LABEL_AREA[area.tipo] : ''}</dd></div>
              <div><dt>Processo</dt><dd>{atividade.processo}</dd></div>
              <div><dt>Responsável</dt><dd>{responsavel?.nome}</dd></div>
              <div><dt>Revisor</dt><dd>{revisor?.nome ?? '—'}</dd></div>
              <div><dt>Aprovador</dt><dd>{aprovador?.nome ?? '—'}</dd></div>
              <div><dt>Status</dt><dd><StatusBadge status={atividade.status} /></dd></div>
              <div><dt>Prioridade</dt><dd>{LABEL_PRIORIDADE[atividade.prioridade]}</dd></div>
              <div><dt>Criticidade</dt><dd><RiscoBadge criticidade={atividade.criticidade} /></dd></div>
              <div><dt>Progresso</dt><dd>{atividade.progresso}%</dd></div>
              <div><dt>Data planejada</dt><dd>{formatarData(atividade.dataPlanejada)}</dd></div>
              <div><dt>Data prevista</dt><dd>{formatarData(atividade.dataPrevista)}</dd></div>
              <div><dt>Data realizada</dt><dd>{atividade.dataRealizada ? formatarData(atividade.dataRealizada) : '—'}</dd></div>
              <div><dt>Prazo em dias úteis</dt><dd>{atividade.prazoDiasUteis}</dd></div>
              {atividade.diaNaoUtilAjustado && (
                <div><dt>Ajuste de prazo</dt><dd>{formatarData(atividade.diaNaoUtilAjustado.dataOriginal)} → {formatarData(atividade.diaNaoUtilAjustado.dataAjustada)} ({atividade.diaNaoUtilAjustado.motivo})</dd></div>
              )}
              <div><dt>Impacto no fechamento</dt><dd>{atividade.impactoNoFechamento}</dd></div>
              <div><dt>Próxima ação</dt><dd>{atividade.proximaAcao}</dd></div>
            </dl>
          )}

          {aba === 'dependencias' && (
            <div className='mc-drawer__dependencias'>
              <h3>Atividades anteriores (impedem o início)</h3>
              {dependenciasAnteriores.length === 0 && <p className='mc-texto-neutro'>Nenhuma dependência anterior.</p>}
              {dependenciasAnteriores.map(dep => {
                const origem = atividades.find(a => a.id === dep.atividadeOrigemId);
                const areaResp = getArea(dep.areaResponsavelId);
                const respo = getUsuario(dep.responsavelId);
                return (
                  <div className='mc-drawer__dependencia-item' key={dep.id}>
                    <button type='button' onClick={() => abrirAtividade(dep.atividadeOrigemId)}>{origem?.nome}</button>
                    <p>{dep.motivo}</p>
                    <small>Área: {areaResp ? LABEL_AREA[areaResp.tipo] : ''} · Responsável: {respo?.nome} · Prazo original: {formatarData(dep.prazoOriginal)} · Previsão atual: {formatarData(dep.previsaoAtual)}</small>
                    <small>{dep.quantidadeAtividadesImpactadas} atividade(s) impactada(s) · {dep.impactoNaPrevisaoFinalDias} dia(s) útil(eis) de impacto na previsão final</small>
                    <small><strong>Ação recomendada:</strong> {dep.acaoRecomendada}</small>
                  </div>
                );
              })}
              <h3>Atividades posteriores (aguardam esta atividade)</h3>
              {dependenciasPosteriores.length === 0 && <p className='mc-texto-neutro'>Nenhuma atividade aguardando esta.</p>}
              {dependenciasPosteriores.map(dep => {
                const destino = atividades.find(a => a.id === dep.atividadeDestinoId);
                return (
                  <div className='mc-drawer__dependencia-item' key={dep.id}>
                    <button type='button' onClick={() => abrirAtividade(dep.atividadeDestinoId)}>{destino?.nome}</button>
                    <small>Efeito no fechamento: represa até esta atividade concluir.</small>
                  </div>
                );
              })}
            </div>
          )}

          {aba === 'checklist' && (
            <div className='mc-drawer__checklist'>
              {carregandoAbaDados && <p className='mc-texto-neutro'>Carregando...</p>}
              {!carregandoAbaDados && checklist.length === 0 && <p className='mc-texto-neutro'>Nenhum item de checklist cadastrado.</p>}
              {checklist.map(item => (
                <div className='mc-checklist-item' key={item.id}>
                  <StatusBadge status={item.status} />
                  <div>
                    <strong>{item.descricao}</strong>
                    <small>{item.responsavelId ? getUsuario(item.responsavelId)?.nome : 'Sem responsável'} · Prazo: {item.prazo ? formatarData(item.prazo) : '—'}{item.evidenciaObrigatoria ? ' · Evidência obrigatória' : ''}</small>
                  </div>
                  {item.status !== 'CONCLUIDA' && (
                    <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => concluirItemChecklist(item.id)}>Concluir</button>
                  )}
                </div>
              ))}
              <div className='mc-drawer__novo-item-checklist'>
                <input type='text' placeholder='Descrição do novo item...' value={novoItemDescricao} onChange={e => setNovoItemDescricao(e.target.value)} />
                <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={!novoItemDescricao.trim()} onClick={criarItemChecklist}>Adicionar</button>
              </div>
            </div>
          )}

          {aba === 'evidencias' && (
            <div className='mc-drawer__evidencias'>
              {carregandoAbaDados && <p className='mc-texto-neutro'>Carregando...</p>}
              {!carregandoAbaDados && evidencias.length === 0 && <p className='mc-texto-neutro'>Nenhuma evidência anexada.</p>}
              {evidencias.map(ev => (
                <div className='mc-evidencia-item' key={ev.id}>
                  <i className='bi bi-file-earmark' />
                  <div>
                    <strong>{ev.arquivo}</strong>
                    <small>{getUsuario(ev.usuarioId)?.nome} · {formatarData(ev.data)}</small>
                    {ev.motivoRejeicao && <small className='mc-texto-warning'>Rejeitada: {ev.motivoRejeicao}</small>}
                  </div>
                  <span className={`mc-badge mc-badge--${ev.statusValidacao === 'VALIDADA' ? 'success' : ev.statusValidacao === 'REJEITADA' ? 'danger' : 'warning'}`}>{ev.statusValidacao.replace('_', ' ')}</span>
                </div>
              ))}
              <label className='mc-btn mc-btn--secondary'>
                <i className='bi bi-upload' /> Anexar evidência
                <input type='file' style={{ display: 'none' }} onChange={anexarArquivo} />
              </label>
            </div>
          )}

          {aba === 'excecoes' && (
            <div className='mc-drawer__excecoes'>
              {carregandoAbaDados && <p className='mc-texto-neutro'>Carregando...</p>}
              {!carregandoAbaDados && excecoes.length === 0 && !mostrarNovaExcecao && <p className='mc-texto-neutro'>Nenhuma exceção concedida para esta atividade.</p>}
              {excecoes.map(exc => (
                <div className='mc-excecao-item' key={exc.id}>
                  <div className='mc-excecao-item__topo'>
                    <strong>{LABEL_TIPO_EXCECAO[exc.tipo]}</strong>
                    <span className={`mc-badge mc-badge--${exc.ativa ? 'warning' : 'neutro'}`}>{exc.ativa ? 'Vigente' : 'Revogada'}</span>
                  </div>
                  <p>{exc.justificativa}</p>
                  <small>Solicitado por {getUsuario(exc.solicitanteUsuarioId)?.nome ?? `#${exc.solicitanteUsuarioId}`} · Autorizado por {getUsuario(exc.autorizadorId)?.nome ?? `#${exc.autorizadorId}`} · Vigência até {formatarData(exc.vigenciaAte)}</small>
                  {exc.revogadaEm && (
                    <small className='mc-texto-warning'>
                      Revogada {exc.revogadaPorUsuarioId ? `por ${getUsuario(exc.revogadaPorUsuarioId)?.nome ?? `#${exc.revogadaPorUsuarioId}`} ` : ''}em {formatarData(exc.revogadaEm)} — {exc.revogadaMotivo}
                    </small>
                  )}
                  {exc.ativa && (
                    <div className='mc-excecao-item__revogar'>
                      <input type='text' placeholder='Motivo da revogação...' value={motivoRevogacao[exc.id] ?? ''} onChange={e => setMotivoRevogacao(prev => ({ ...prev, [exc.id]: e.target.value }))} />
                      <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' disabled={!(motivoRevogacao[exc.id] ?? '').trim()} onClick={() => revogarExcecao(exc.id)}>Revogar</button>
                    </div>
                  )}
                </div>
              ))}

              {mostrarNovaExcecao ? (
                <div className='mc-form-inline'>
                  <label>Tipo
                    <select value={novaExcecao.tipo} onChange={e => setNovaExcecao({ ...novaExcecao, tipo: e.target.value as TipoExcecao })}>
                      {(Object.keys(LABEL_TIPO_EXCECAO) as TipoExcecao[]).map(t => <option key={t} value={t}>{LABEL_TIPO_EXCECAO[t]}</option>)}
                    </select>
                  </label>
                  {novaExcecao.tipo === 'DEPENDENCIA_BLOQUEANTE' && (
                    <label>Dependência
                      <select value={novaExcecao.dependenciaId} onChange={e => setNovaExcecao({ ...novaExcecao, dependenciaId: e.target.value })}>
                        <option value=''>Selecione...</option>
                        {dependenciasAnteriores.map(dep => {
                          const origem = atividades.find(a => a.id === dep.atividadeOrigemId);
                          return <option key={dep.id} value={dep.id}>{origem?.nome ?? `#${dep.id}`}</option>;
                        })}
                      </select>
                    </label>
                  )}
                  <label>Autorizador
                    <select value={novaExcecao.autorizadorId} onChange={e => setNovaExcecao({ ...novaExcecao, autorizadorId: e.target.value })}>
                      <option value=''>Selecione...</option>
                      {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </select>
                  </label>
                  <label>Vigência até
                    <input type='datetime-local' value={novaExcecao.vigenciaAte} onChange={e => setNovaExcecao({ ...novaExcecao, vigenciaAte: e.target.value })} />
                  </label>
                  <label>Justificativa
                    <input type='text' placeholder='Motivo da exceção...' value={novaExcecao.justificativa} onChange={e => setNovaExcecao({ ...novaExcecao, justificativa: e.target.value })} />
                  </label>
                  <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' disabled={!novaExcecao.justificativa.trim() || !novaExcecao.autorizadorId || !novaExcecao.vigenciaAte} onClick={concederExcecao}>Conceder</button>
                  <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setMostrarNovaExcecao(false)}>Cancelar</button>
                </div>
              ) : (
                <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setMostrarNovaExcecao(true)}><i className='bi bi-plus-lg' /> Conceder exceção</button>
              )}
            </div>
          )}

          {aba === 'comunicacao' && (
            <div className='mc-drawer__comunicacao'>
              {carregandoAbaDados && <p className='mc-texto-neutro'>Carregando...</p>}
              {!carregandoAbaDados && comentarios.length === 0 && <p className='mc-texto-neutro'>Nenhum comentário ainda.</p>}
              {comentarios.map(c => (
                <div className='mc-comentario' key={c.id}>
                  <strong>{getUsuario(c.autorId)?.nome}</strong>
                  <span>{c.tipo}</span>
                  <p>{c.texto}</p>
                  <small>{formatarData(c.data)}</small>
                </div>
              ))}
              <div className='mc-drawer__novo-comentario'>
                <textarea rows={3} placeholder='Escreva um comentário, solicitação ou decisão...' value={novoComentario} onChange={e => setNovoComentario(e.target.value)} />
                <button type='button' className='mc-btn mc-btn--primary mc-btn--sm' onClick={enviarComentario}>Enviar</button>
              </div>
            </div>
          )}

          {aba === 'historico' && (
            <div className='mc-drawer__historico'>
              {carregandoAbaDados && <p className='mc-texto-neutro'>Carregando...</p>}
              {!carregandoAbaDados && historico.length === 0 && <p className='mc-texto-neutro'>Nenhum registro de alteração ainda.</p>}
              {historico.map(log => (
                <div className='mc-log-item' key={log.id}>
                  <i className='bi bi-clock-history' />
                  <div>
                    <strong>{getUsuario(log.usuarioId)?.nome}</strong> — <strong>{log.acao.replace(/_/g, ' ')}</strong>
                    {log.statusAnterior && log.statusNovo && <small>{log.statusAnterior} → {log.statusNovo}</small>}
                    {log.detalhe && <small>{log.detalhe}</small>}
                    <small>{formatarData(log.criadoEm)}</small>
                  </div>
                </div>
              ))}
            </div>
          )}

          {aba === 'inteligencia' && (
            <PainelInteligencia recomendacoes={recomendacoesDaAtividade} onAbrirAtividade={abrirAtividade} />
          )}

          {aba === 'produto' && (
            produtoRelacionado
              ? <ProdutoCard produto={produtoRelacionado} />
              : <p className='mc-texto-neutro'>{atividade.produtoIntegradoTipo ? `Sem dado atual de ${LABEL_PRODUTO[atividade.produtoIntegradoTipo]} para esta empresa.` : 'Esta atividade não está vinculada a nenhum produto da AI Númera — é uma atividade manual/interna.'}</p>
          )}
        </div>
      </aside>
    </div>
  );
};

export default AtividadeDrawer;
